import { EnrichedVariable } from '@/components/template-builder/utils/variables-list';

/**
 * Shared logic of the variable format panel (VariableFormatPanel.vue) :
 * builds the final `{var=path modifier=value…}` tag from the panel state,
 * decides on which channels the panel is available, and suggests a format
 * from the variable type. The rendering itself (send time) is done by the
 * backend sanitizeTemplates.service ; the preview mirror lives in
 * template-builder/utils/sanitizer.ts (formatVariableValue).
 */

export type VariableFormatKind = 'none' | 'currency' | 'date' | 'text' | 'truncate';

export interface VariableFormatConfig {
  format: VariableFormatKind;
  // currency
  currencyLang: 'locale' | 'shop';
  currencyCode: string; // '' = shop currency
  multiplyFactor: string; // '' or '1' = no unit conversion
  // date
  datePreset: 'short' | 'long' | 'datetime' | 'custom';
  dateLang: 'locale' | 'shop';
  dateCustomTokens: string;
  // text
  caseMode: 'upper' | 'lower' | 'capitalize';
  // truncate
  truncateLimit: number;
  // fallback when the variable has no value (independent from the format)
  defaultValue: string;
}

export const createDefaultFormatConfig = (): VariableFormatConfig => ({
  format: 'none',
  currencyLang: 'locale',
  currencyCode: '',
  multiplyFactor: '',
  datePreset: 'short',
  dateLang: 'locale',
  dateCustomTokens: 'DD/MM/YYYY',
  caseMode: 'capitalize',
  truncateLimit: 50,
  defaultValue: '',
});

/**
 * Splits a picker value like '{var=product.image_url resize=400x400}' into
 * the dotted path and the already-present modifiers (kept untouched)
 */
export const parseVariableValue = (value: string): { path: string; existingModifiers: string } => {
  const match = value.match(/^\{(?:var=)?"?([^"\s}]+)"?(\s[^}]+)?\}$/);
  if (!match) {
    return { path: value, existingModifiers: '' };
  }
  return { path: match[1], existingModifiers: (match[2] || '').trim() };
};

// The quoted-value syntax accepts neither double quotes nor closing braces
const sanitizeModifierValue = (value: string): string => value.replace(/["}]/g, '').trim();

const isMeaningfulFactor = (factor: string): boolean => {
  const parsed = Number(factor);
  return factor.trim() !== '' && Number.isFinite(parsed) && parsed !== 1 && parsed !== 0;
};

/**
 * Modifiers added by the panel configuration, as normalized [full, name, value]
 * entries — the same shape the sanitizer produces when parsing a template
 * (reused directly by the live preview)
 */
export const buildConfigModifiers = (config: VariableFormatConfig): string[][] => {
  const modifiers: string[][] = [];
  const push = (name: string, rawValue: string, quote = false) => {
    const modifierValue = sanitizeModifierValue(rawValue);
    if (modifierValue === '') {
      return;
    }
    modifiers.push([`${name}=${quote ? `"${modifierValue}"` : modifierValue}`, name, modifierValue]);
  };

  if (config.format === 'currency') {
    // multiply is emitted before currency : modifiers apply in tag order
    if (isMeaningfulFactor(config.multiplyFactor)) {
      push('multiply', String(Number(config.multiplyFactor)));
    }
    push('currency', config.currencyCode !== '' ? config.currencyCode.toUpperCase() : config.currencyLang);
  } else if (config.format === 'date') {
    if (config.datePreset === 'short') {
      push('date_format', config.dateLang);
    } else if (config.datePreset === 'datetime') {
      push('datetime_format', config.dateLang);
    } else if (config.datePreset === 'long') {
      // Free moment tokens are always rendered in the contact language
      push('date_format', 'D MMMM YYYY', true);
    } else {
      push('date_format', config.dateCustomTokens, true);
    }
  } else if (config.format === 'text') {
    push('case', config.caseMode);
  } else if (config.format === 'truncate' && config.truncateLimit > 0) {
    push('truncate', String(config.truncateLimit));
  }

  if (config.defaultValue.trim() !== '') {
    push('default', config.defaultValue, true);
  }

  return modifiers;
};

/**
 * Final tag inserted in the template : original path + modifiers already
 * carried by the picker value (resize…) + the ones of the configuration
 */
export const buildVariableTag = (variableValue: string, config: VariableFormatConfig): string => {
  const { path, existingModifiers } = parseVariableValue(variableValue);
  const configModifiers = buildConfigModifiers(config).map((modifier) => modifier[0]);
  const allModifiers = [existingModifiers, ...configModifiers].filter((part) => part !== '').join(' ');

  return `{var=${path}${allModifiers !== '' ? ` ${allModifiers}` : ''}}`;
};

/**
 * Format suggested by the variable type — a hint only, never a restriction
 */
export const suggestedFormatFor = (variable: EnrichedVariable): VariableFormatKind | null => {
  if (variable.preformatted) {
    return null;
  }
  if (variable.type && ['number', 'decimal'].includes(variable.type)) {
    return 'currency';
  }
  if (variable.type && ['date', 'datetime'].includes(variable.type)) {
    return 'date';
  }
  return null;
};

/**
 * The format panel only makes sense on channels rendered by the v5 engine.
 * Templates carrying version 4 content ARE rendered by it (sanitizeTemplates
 * explicitly handles the V3/V4 syntaxes — see SC-3761), so they get the panel
 * too. Excluded : teasers (not rendered by formatTemplateBody) and WhatsApp
 * (its helper has its own variable engine without modifiers). Outside the
 * template builder (campaign subjects…) headers go through the v5 engine.
 */
export const isFormatPanelAvailable = (): boolean => {
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  const { TemplateEditorState } = require('@/composables/template-editor/TemplateEditor');
  const { template } = TemplateEditorState;

  if (!template) {
    return true;
  }

  return template.type !== 'teaser' && template.type !== 'whatsapp';
};
